'use client';

import { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { useSDKStore } from '@/lib/sdk/store';
import {
  Terminal,
  CheckCircle2,
  XCircle,
  Clock,
  Shield,
  Monitor,
  MapPin,
} from 'lucide-react';

interface DeviceAuthPageProps {
  initialCode?: string;
}

export function DeviceAuthPage({ initialCode }: DeviceAuthPageProps) {
  const {
    cliSession,
    cliSessionLoading,
    cliSessionError,
    loadCLISession,
    authorizeCLI,
    denyCLI,
    clearCLISession,
  } = useSDKStore();

  const [userCode, setUserCode] = useState(initialCode || '');
  const [submitted, setSubmitted] = useState(!!initialCode);
  const [authorizing, setAuthorizing] = useState(false);

  useEffect(() => {
    if (initialCode) {
      loadCLISession(initialCode);
    }
    return () => clearCLISession();
  }, [initialCode, loadCLISession, clearCLISession]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (userCode.trim()) {
      setSubmitted(true);
      loadCLISession(userCode.trim().toUpperCase());
    }
  };

  const handleAuthorize = async () => {
    setAuthorizing(true);
    try {
      await authorizeCLI(userCode);
    } catch (error) {
      // Error handled by store
    } finally {
      setAuthorizing(false);
    }
  };

  const handleDeny = async () => {
    setAuthorizing(true);
    try {
      await denyCLI(userCode);
    } catch (error) {
      // Error handled by store
    } finally {
      setAuthorizing(false);
    }
  };

  // Success/Denied states
  if (cliSession?.status === 'authorized') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 text-center">
            <CheckCircle2 className="size-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-2">Device Authorized</h2>
            <p className="text-muted-foreground mb-4">
              You can now close this window and return to your terminal.
            </p>
            <Badge variant="secondary" className="gap-1">
              <Terminal className="size-3" />
              {cliSession.device_name || 'CLI Device'}
            </Badge>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (cliSession?.status === 'denied') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 text-center">
            <XCircle className="size-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-2">Authorization Denied</h2>
            <p className="text-muted-foreground">
              The device authorization request was denied.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (cliSession?.status === 'expired') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 text-center">
            <Clock className="size-16 text-yellow-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-2">Code Expired</h2>
            <p className="text-muted-foreground mb-4">
              This authorization code has expired. Please generate a new code from the CLI.
            </p>
            <Button variant="outline" onClick={() => {
              setSubmitted(false);
              setUserCode('');
              clearCLISession();
            }}>
              Try Again
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="size-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Terminal className="size-8 text-primary" />
          </div>
          <CardTitle>Authorize Device</CardTitle>
          <CardDescription>
            {submitted
              ? 'Review the device details and authorize access'
              : 'Enter the code displayed in your terminal'}
          </CardDescription>
        </CardHeader>

        <CardContent>
          {cliSessionError && (
            <Alert variant="destructive" className="mb-4">
              <AlertDescription>{cliSessionError}</AlertDescription>
            </Alert>
          )}

          {!submitted ? (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="user-code">Device Code</Label>
                <Input
                  id="user-code"
                  value={userCode}
                  onChange={(e) => setUserCode(e.target.value.toUpperCase())}
                  placeholder="ABCD-EFGH"
                  className="mt-1 text-center text-2xl font-mono tracking-widest"
                  maxLength={9}
                  autoFocus
                />
              </div>
              <Button type="submit" className="w-full" disabled={!userCode.trim()}>
                Continue
              </Button>
            </form>
          ) : cliSessionLoading ? (
            <div className="space-y-4">
              <Skeleton className="h-20 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : cliSession?.status === 'pending' ? (
            <div className="space-y-6">
              {/* Device Info */}
              <div className="bg-muted/50 rounded-lg p-4 space-y-3">
                <h4 className="font-medium text-sm flex items-center gap-2">
                  <Monitor className="size-4" />
                  Device Information
                </h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="text-muted-foreground">Device</div>
                  <div>{cliSession.device_name || 'CLI Terminal'}</div>
                  {cliSession.ip_address && (
                    <>
                      <div className="text-muted-foreground">IP Address</div>
                      <div className="flex items-center gap-1">
                        <MapPin className="size-3" />
                        {cliSession.ip_address}
                      </div>
                    </>
                  )}
                  <div className="text-muted-foreground">Expires</div>
                  <div>{new Date(cliSession.expires_at).toLocaleTimeString()}</div>
                </div>
              </div>

              {/* Requested Scopes */}
              <div className="space-y-2">
                <h4 className="font-medium text-sm flex items-center gap-2">
                  <Shield className="size-4" />
                  Requested Permissions
                </h4>
                <div className="flex flex-wrap gap-2">
                  {cliSession.scopes.map((scope) => (
                    <Badge key={scope} variant="secondary">
                      {scope}
                    </Badge>
                  ))}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={handleDeny}
                  disabled={authorizing}
                >
                  Deny
                </Button>
                <Button
                  className="flex-1"
                  onClick={handleAuthorize}
                  disabled={authorizing}
                >
                  {authorizing ? 'Authorizing...' : 'Authorize'}
                </Button>
              </div>
            </div>
          ) : (
            <div className="text-center py-4">
              <p className="text-muted-foreground">
                Session not found. Please check the code and try again.
              </p>
              <Button
                variant="outline"
                className="mt-4"
                onClick={() => {
                  setSubmitted(false);
                  setUserCode('');
                  clearCLISession();
                }}
              >
                Try Again
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
